import type { ReactNode } from "react";
import { AlertTriangle, Copy, Plus, Trash2 } from "lucide-react";

export function Section({
  title,
  description,
  icon,
  action,
  children,
  tone = "default",
}: {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  tone?: "default" | "accent";
}) {
  return (
    <section className={`section-card section-card--${tone}`}>
      <header className="section-card__header">
        <div className="section-card__heading">
          {icon ? <span className="section-card__icon">{icon}</span> : null}
          <div>
            <h2>{title}</h2>
            {description ? <p>{description}</p> : null}
          </div>
        </div>
        {action}
      </header>
      <div className="section-card__body">{children}</div>
    </section>
  );
}

export function MoneyInput({
  label,
  value,
  onChange,
  min = 0,
  required,
  hint,
  allowEmpty,
}: {
  label: string;
  min?: number;
  required?: boolean;
  hint?: string;
} & (
  | { allowEmpty: true; value: number | null; onChange: (value: number | null) => void }
  | { allowEmpty?: false; value: number; onChange: (value: number) => void }
)) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <span className="money-input">
        <input
          type="number"
          inputMode="numeric"
          min={min}
          step="1"
          value={value == null ? (allowEmpty ? "" : 0) : Number.isFinite(value) ? value : 0}
          placeholder={allowEmpty ? "未入力" : undefined}
          required={required}
          onChange={(event) => {
            const next = event.target.value;
            if (allowEmpty) onChange(next === "" ? null : Number(next));
            else onChange(next === "" ? 0 : Number(next));
          }}
        />
        <span>円</span>
      </span>
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

export function AddButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button type="button" className="button button--quiet button--small" onClick={onClick}>
      <Plus size={16} aria-hidden="true" />
      {children}
    </button>
  );
}

export function RowActions({
  onDuplicate,
  onRemove,
  disableRemove,
}: {
  onDuplicate?: () => void;
  onRemove: () => void;
  disableRemove?: boolean;
}) {
  return (
    <div className="row-actions">
      {onDuplicate ? (
        <button type="button" className="icon-button" onClick={onDuplicate} aria-label="行を複製" title="複製">
          <Copy size={16} aria-hidden="true" />
        </button>
      ) : null}
      <button
        type="button"
        className="icon-button icon-button--danger"
        onClick={onRemove}
        disabled={disableRemove}
        aria-label="行を削除"
        title="削除"
      >
        <Trash2 size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

export function ErrorSummary({ messages }: { messages: string[] }) {
  if (messages.length === 0) return null;
  return (
    <div className="error-summary" role="alert" aria-live="assertive">
      <AlertTriangle size={20} aria-hidden="true" />
      <div>
        <strong>保存前に確認してください</strong>
        <ul>
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function LoadingBlock({ label = "読み込み中" }: { label?: string }) {
  return (
    <div className="loading-block" role="status">
      <span className="spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}
